import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Tag, DatePicker, InputNumber, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getUserTenantPageList, getUserTenantDetail, addUserTenant, updateUserTenant, updateUserTenantStatus } from '../../api/userTenant';
import './index.css';
import dayjs from 'dayjs';

// antd Select.Option 在部分 TS 配置下类型较严格，这里用 any 避免影响页面逻辑
const Option: any = (Select as any).Option;

const UserTenants = () => {
  const [userTenants, setUserTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUserTenant, setEditingUserTenant] = useState(null);
  const [searchParams, setSearchParams] = useState({
    userId: null,
    tenantId: null,
    employeeNo: '',
    department: '',
    status: null,
    isMainTenant: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [form] = Form.useForm();

  // 表格列定义
  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: '租户ID',
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: 100,
    },
    {
      title: '租户名称',
      dataIndex: 'tenantName',
      key: 'tenantName',
      width: 160,
      render: (text) => text || '-',
    },
    {
      title: '主租户',
      dataIndex: 'isMainTenant',
      key: 'isMainTenant',
      width: 100,
      render: (isMain) => (
        <Tag color={isMain === 1 ? 'blue' : 'default'}>
          {isMain === 1 ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '工号',
      dataIndex: 'employeeNo',
      key: 'employeeNo',
      width: 120,
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      width: 150,
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
      width: 150,
    },
    {
      title: '入职时间',
      dataIndex: 'joinTime',
      key: 'joinTime',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '离职时间',
      dataIndex: 'leaveTime',
      key: 'leaveTime',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          0: { text: '禁用', color: 'red' },
          1: { text: '启用', color: 'green' },
        };
        const statusInfo = statusMap[status] || { text: '未知', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '创建人',
      dataIndex: 'createUserName',
      key: 'createUserName',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: '更新人',
      dataIndex: 'cpdateUserName',
      key: 'cpdateUserName',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleTogglestatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
        </Space>
      ),
    },
  ];

  // 获取用户租户列表
  const fetchUserTenants = async () => {
    setLoading(true);
    try {
      const params = {
        PageIndex: pagination.current,
        PageSize: pagination.pageSize,
        ...searchParams,
      };
      
      const response: any = await getUserTenantPageList(params);
      
      if (response?.res_status_code === '0' && response?.res_content) {
        setUserTenants(response.res_content.items || []);
        const rawTotal =
          response?.res_content?.totalCount ??
          response?.res_content?.TotalCount ??
          response?.totalCount ??
          response?.TotalCount ??
          response?.res_content?.total ??
          response?.res_content?.Total ??
          0;
        const total = Number(rawTotal) || 0;
        // 只更新 total，避免用旧闭包覆盖 current/pageSize，导致无法翻页
        setPagination((prev) => ({ ...prev, total }));
      } else {
        message.error(response?.res_message || '获取用户租户列表失败');
      }
    } catch (error) {
      console.error('获取用户租户列表失败:', error);
      message.error('获取用户租户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserTenants();
  }, [pagination.current, pagination.pageSize]);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchUserTenants();
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      userId: null,
      tenantId: null,
      employeeNo: '',
      department: '',
      status: null,
      isMainTenant: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      fetchUserTenants();
    }, 100);
  };

  // 新增用户租户
  const handleAdd = () => {
    setEditingUserTenant(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑用户租户
  const handleEdit = async (record) => {
    try {
      const response: any = await getUserTenantDetail(record.id);
      if (response?.res_status_code === '0' && response?.res_content) {
        const userTenant = response.res_content;
        setEditingUserTenant(userTenant);
        form.setFieldsValue({
          ...userTenant,
          joinTime: userTenant.joinTime ? dayjs(userTenant.joinTime) : null,
          leaveTime: userTenant.leaveTime ? dayjs(userTenant.leaveTime) : null,
        });
        setModalVisible(true);
      } else {
        message.error(response?.res_message || '获取用户租户详情失败');
      }
    } catch (error) {
      console.error('获取用户租户详情失败:', error);
      message.error('获取用户租户详情失败');
    }
  };

  // 保存用户租户
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        joinTime: values.joinTime ? values.joinTime.format('YYYY-MM-DD HH:mm:ss') : null,
        leaveTime: values.leaveTime ? values.leaveTime.format('YYYY-MM-DD HH:mm:ss') : null,
        isMainTenant: values.isMainTenant ? 1 : 0,
      };

      if (editingUserTenant) {
        // 更新
        data.Id = editingUserTenant.id;
        const response: any = await updateUserTenant(data);
        if (response?.res_status_code === '0') {
          message.success('更新用户租户成功');
          setModalVisible(false);
          fetchUserTenants();
        } else {
          message.error(response?.res_message || '更新用户租户失败');
        }
      } else {
        // 新增
        const response: any = await addUserTenant(data);
        if (response?.res_status_code === '0') {
          message.success('添加用户租户成功');
          setModalVisible(false);
          fetchUserTenants();
        } else {
          message.error(response?.res_message || '添加用户租户失败');
        }
      }
    } catch (error) {
      console.error('保存用户租户失败:', error);
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error('保存用户租户失败');
    }
  };

  // 切换状态
  const handleTogglestatus = async (record) => {
    try {
      const newstatus = record.status === 1 ? 0 : 1;
      const response: any = await updateUserTenantStatus({
        Status: newstatus,
        Ids: [record.id],
      });
      if (response?.res_status_code === '0') {
        message.success(`${newstatus === 1 ? '启用' : '禁用'}用户租户成功`);
        fetchUserTenants();
      } else {
        message.error(response?.res_message || '更新状态失败');
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 分页变化
  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current:
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  return (
    <div className="user-tenants-page">
      <div className="page-header">
        <h2>用户租户管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增用户租户
        </Button>
      </div>

      {/* 搜索区域 */}
      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="用户ID"
            value={searchParams.userId}
            onChange={(e) => setSearchParams({ ...searchParams, userId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="租户ID"
            value={searchParams.tenantId}
            onChange={(e) => setSearchParams({ ...searchParams, tenantId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="工号"
            value={searchParams.employeeNo}
            onChange={(e) => setSearchParams({ ...searchParams, employeeNo: e.target.value })}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="部门"
            value={searchParams.department}
            onChange={(e) => setSearchParams({ ...searchParams, department: e.target.value })}
            style={{ width: 150 }}
            allowClear
          />
          <Select
            placeholder="状态"
            value={searchParams.status}
            onChange={(value) => setSearchParams({ ...searchParams, status: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
          <Select
            placeholder="主租户"
            value={searchParams.isMainTenant}
            onChange={(value) => setSearchParams({ ...searchParams, isMainTenant: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value={1}>是</Option>
            <Option value={0}>否</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={userTenants}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1900, y: 'calc(100vh - 320px)' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingUserTenant ? '编辑用户租户' : '新增用户租户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
          initialValues={{
            status: 1,
            isMainTenant: false,
          }}
        >
          <Form.Item
            name="userId"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="请输入用户ID" />
          </Form.Item>
          <Form.Item
            name="username"
            label="用户名"
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="tenantId"
            label="租户ID"
            rules={[{ required: true, message: '请输入租户ID' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="请输入租户ID" />
          </Form.Item>
          <Form.Item
            name="tenantName"
            label="租户名称"
          >
            <Input placeholder="请输入租户名称" />
          </Form.Item>
          <Form.Item
            name="isMainTenant"
            label="主租户"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="employeeNo"
            label="工号"
          >
            <Input placeholder="请输入工号" />
          </Form.Item>
          <Form.Item
            name="department"
            label="部门"
          >
            <Input placeholder="请输入部门" />
          </Form.Item>
          <Form.Item
            name="position"
            label="职位"
          >
            <Input placeholder="请输入职位" />
          </Form.Item>
          <Form.Item
            name="joinTime"
            label="入职时间"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
          <Form.Item
            name="leaveTime"
            label="离职时间"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
          >
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="LeaderuserId"
            label="上级用户ID"
          >
            <InputNumber style={{ width: '100%' }} placeholder="请输入上级用户ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserTenants;
